import axios from 'axios';

// This would normally be in an environment variable
const ANTHROPIC_API_KEY = 'your-api-key-here';
const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';

// Define types for the API
interface AnthropicMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AnthropicRequest {
  model: string;
  messages: AnthropicMessage[];
  max_tokens: number;
}

interface AnthropicResponse {
  content: string;
  model: string;
  stop_reason: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

/**
 * Send a message to the Anthropic API and get a response
 */
export const sendMessageToAnthropic = async (messages: AnthropicMessage[]): Promise<string> => {
  try {
    // For demo purposes, if API key is not set, return a mock response
    if (ANTHROPIC_API_KEY === 'your-api-key-here') {
      console.log('Using mock response - replace with your actual Anthropic API key');
      return getMockResponse(messages[messages.length - 1].content);
    }

    const response = await axios.post<AnthropicResponse>(
      ANTHROPIC_API_URL,
      {
        model: 'claude-3-opus-20240229',
        messages: messages,
        max_tokens: 1000,
      } as AnthropicRequest,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01'
        },
      }
    );

    return response.data.content;
  } catch (error) {
    console.error('Error calling Anthropic API:', error);
    return 'I apologize, but I encountered an error processing your request. Please try again later.';
  }
};

/**
 * Get a mock response for demo purposes when API key is not set
 */
const getMockResponse = (userMessage: string): string => {
  // Healthcare-specific responses
  const responses = [
    "Based on your wellness metrics, I notice you've been consistently practicing mindfulness. That's excellent for stress management. Would you like to try an advanced technique?",
    "I understand the challenges of balancing clinical responsibilities. Have you considered joining our 'Physician Wellness Circle' that meets on Thursdays?",
    "Your question touches on an important aspect of clinician wellbeing. Research shows that brief moments of connection with colleagues can significantly reduce burnout.",
    "Looking at the SentientIQ platform data, clinicians who participate in at least two wellness activities per week show a 30% reduction in reported stress levels.",
    "That's a great question about work-life integration. Many of your colleagues have found success with the 'boundaries technique' we discuss in our resilience workshop.",
    "I can help you find resources for that. The Methodist Health System offers several programs specifically designed for clinician wellness in that area.",
    "Your commitment to professional development while maintaining wellness is admirable. Have you explored our new 'Balanced Growth' pathway in the learning center?",
    "I notice you've been engaging with our mindfulness content. Would you like me to recommend some advanced practices that take only 2-3 minutes between patients?",
    "That's an excellent observation about clinical workflow. Would you like me to connect you with our practice efficiency specialist who has helped many physicians optimize their EMR use?",
    "Based on your interaction patterns, you might benefit from our 'Micro-Resilience' techniques designed specifically for high-intensity clinical environments."
  ];
  
  // For demo purposes, return a random response
  return responses[Math.floor(Math.random() * responses.length)];
};
